(() => {
  const ROOT_SELECTOR = '[data-hk-accessory-upsell]';
  const RUNTIME_KEY = '__HK_ACCESSORY_UPSELL_COMPANION_V18__';

  const runtime = window[RUNTIME_KEY] ||= {
    controllers: new Set(),
    initialized: false,
    pubsubUnsubscribe: null,
    lastProductCartByVariant: new Map()
  };

  function parseIds(value) {
    return new Set(
      String(value || '')
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean)
    );
  }

  function extractAddedVariantIds(payload) {
    const ids = new Set();
    const collect = (item) => {
      if (!item || typeof item !== 'object') return;
      [item.variant_id, item.variantId, item.id].forEach((id) => {
        if (id !== undefined && id !== null && String(id).trim()) ids.add(String(id));
      });
    };

    if (Array.isArray(payload)) payload.forEach(collect);
    else if (payload && typeof payload === 'object') {
      collect(payload);
      if (Array.isArray(payload.items)) payload.items.forEach(collect);
    }
    return ids;
  }

  function findController(ids) {
    for (const controller of runtime.controllers) {
      if (!controller?.root?.isConnected) continue;
      for (const id of ids) {
        if (controller.mainVariantIds.has(String(id))) return controller;
      }
    }
    return null;
  }

  async function parseJsonResponse(response, context) {
    const text = await response.text();
    let parsed = null;

    try {
      parsed = text ? JSON.parse(text) : {};
    } catch (_) {
      const error = new Error(
        response.status === 429
          ? 'Cart is temporarily busy. Please wait a moment and try again.'
          : `${context} returned an unexpected response.`
      );
      error.status = response.status;
      throw error;
    }

    if (!response.ok || parsed?.status || parsed?.errors) {
      const error = new Error(
        parsed?.description
        || parsed?.message
        || (response.status === 429
          ? 'Cart is temporarily busy. Please wait a moment and try again.'
          : `Unable to ${context.toLowerCase()}.`)
      );
      error.status = response.status;
      throw error;
    }

    return parsed;
  }

  function cartStateFromSections(parsedState) {
    const sections = parsedState?.sections && typeof parsedState.sections === 'object'
      ? parsedState.sections
      : {};
    const drawer = document.querySelector('cart-drawer');
    const sectionId = drawer?.getAttribute('data-section-id');
    let html = sectionId ? sections[sectionId] : '';

    if (typeof html !== 'string' || !html) {
      html = Object.values(sections).find(
        (value) => typeof value === 'string' && value.includes('<cart-drawer')
      ) || '';
    }

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const rows = [...doc.querySelectorAll('cart-drawer [id^="CartDrawer-Item-"][data-variant-id][data-quantity]')];
    const items = rows.map((row) => {
      const variantId = Number(row.getAttribute('data-variant-id')) || 0;
      const properties = {};
      if (row.hasAttribute('data-hk-linked-accessory')) properties._hk_linked_accessory = 'true';
      if (row.hasAttribute('data-hk-linked-main-variant')) {
        properties._hk_linked_main_variant = row.getAttribute('data-hk-linked-main-variant');
      }
      if (row.hasAttribute('data-gift-line')) properties._gift = 'true';
      if (row.hasAttribute('data-gift-for')) properties._gift_for = row.getAttribute('data-gift-for');

      return {
        id: variantId,
        variant_id: variantId,
        quantity: Number(row.getAttribute('data-quantity')) || 0,
        key: String(row.id || '').replace(/^CartDrawer-Item-/, ''),
        properties
      };
    });

    return {
      item_count: items.reduce((sum, item) => sum + item.quantity, 0),
      items,
      sections
    };
  }

  function publishCartUpdate(cart, variantId) {
    if (window.theme?.pubsub?.publish && window.theme?.pubsub?.PUB_SUB_EVENTS?.cartUpdate) {
      window.theme.pubsub.publish(window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
        source: 'hiknight-accessory-upsell',
        productVariantId: String(variantId || ''),
        cart
      });
    }
  }

  function createController(root) {
    const sectionId = String(root.dataset.sectionId || '');
    const productId = String(root.dataset.mainProductId || '');
    const mainVariantIds = parseIds(root.dataset.mainVariantIds);
    const productInfo = document.getElementById(`ProductInfo-${sectionId}-${productId}`);
    const formId = productInfo?.getAttribute('form');
    const form = formId ? document.getElementById(formId) : null;
    const drawer = document.querySelector('cart-drawer');
    const grid = root.querySelector('[data-hk-upsell-grid]');
    const cards = [...root.querySelectorAll('[data-hk-upsell-card]')];
    const closeButtons = [...root.querySelectorAll('[data-hk-upsell-close]')];

    if (!cards.length) return null;

    let isOpen = false;
    let closeTimer = null;
    let activeMainVariantId = '';
    let activeMainQuantity = 1;
    let latestCart = null;
    let requestInFlight = false;
    let lastSubmit = null;

    function getDrawerWidth() {
      if (window.matchMedia('(max-width: 58rem)').matches) return 0;
      if (!drawer?.hasAttribute('open')) return 0;
      const inner = drawer.querySelector('.drawer__inner');
      if (!inner) return 0;
      const rect = inner.getBoundingClientRect();
      return rect.width > 180 ? Math.min(rect.width, window.innerWidth * 0.6) : 0;
    }

    function updateDrawerOffset() {
      root.style.setProperty('--hk-upsell-drawer-width', `${Math.max(0, getDrawerWidth())}px`);
    }

    function cardVariantId(card) {
      const select = card.querySelector('[data-hk-upsell-variant-select]');
      return String(
        select?.value
        || card.querySelector('[data-hk-upsell-add]')?.dataset.variantId
        || card.dataset.defaultVariantId
        || ''
      );
    }

    function cartVariantQuantity(cart, variantId) {
      if (!cart || !Array.isArray(cart.items)) return 0;
      return cart.items.reduce((total, item) => {
        const id = String(item.variant_id || item.id || '');
        return id === String(variantId) ? total + (Number(item.quantity) || 0) : total;
      }, 0);
    }

    function setCardState(card, quantity) {
      const button = card.querySelector('[data-hk-upsell-add]');
      const text = card.querySelector('[data-hk-upsell-add-text]');
      const status = card.querySelector('[data-hk-upsell-status]');
      const inCart = quantity > 0;

      card.classList.toggle('is-in-cart', inCart);
      button?.classList.toggle('is-added', inCart);
      if (text) text.textContent = inCart ? 'Add another' : 'Add';
      if (status && !status.classList.contains('is-error')) {
        status.textContent = inCart ? `In cart: ${quantity}` : '';
      }
    }

    function syncCards(cart) {
      if (!cart) return;
      latestCart = cart;
      cards.forEach((card) => setCardState(card, cartVariantQuantity(cart, cardVariantId(card))));
    }

    function clearInlineSelections() {
      if (!productInfo) return;
      productInfo.querySelectorAll('.paid-accessories-block .accessory-checkbox:checked:not(:disabled)')
        .forEach((input) => {
          input.checked = false;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
    }

    function open({ variantId = '', quantity = 1, cart = null } = {}) {
      activeMainVariantId = String(variantId || activeMainVariantId || '');
      activeMainQuantity = Math.max(1, Number(quantity) || 1);
      if (cart) syncCards(cart);

      if (closeTimer) {
        clearTimeout(closeTimer);
        closeTimer = null;
      }

      root.hidden = false;
      root.classList.toggle('is-mobile', window.matchMedia('(max-width: 58rem)').matches);
      updateDrawerOffset();
      isOpen = true;

      requestAnimationFrame(() => {
        if (!isOpen) return;
        updateDrawerOffset();
        root.classList.add('is-open');
      });
    }

    function close() {
      if (!isOpen && root.hidden) return;
      isOpen = false;
      root.classList.remove('is-open');
      if (closeTimer) clearTimeout(closeTimer);
      closeTimer = window.setTimeout(() => {
        root.hidden = true;
        closeTimer = null;
      }, 230);
    }

    function findLinkedAccessoryLine(cart, variantId) {
      if (!cart || !Array.isArray(cart.items)) return null;
      const target = String(variantId || '');
      return cart.items.find((item) => {
        const itemVariant = String(item.variant_id || item.id || '');
        const props = item.properties || {};
        return itemVariant === target
          && String(props._hk_linked_accessory || '') === 'true'
          && String(props._hk_linked_main_variant || '') === String(activeMainVariantId || '');
      }) || null;
    }

    async function addAccessory(card, variantId) {
      if (requestInFlight) return;
      requestInFlight = true;

      const button = card.querySelector('[data-hk-upsell-add]');
      const status = card.querySelector('[data-hk-upsell-status]');
      button?.setAttribute('aria-busy', 'true');
      if (button) button.disabled = true;
      if (status) {
        status.textContent = 'Adding…';
        status.classList.remove('is-error');
      }

      try {
        const sections = [];
        document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', {
          bubbles: true,
          detail: { sections }
        }));

        const config = window.theme?.utils?.fetchConfig
          ? window.theme.utils.fetchConfig('javascript')
          : { method: 'POST', headers: { Accept: 'application/json' } };
        config.headers ||= {};
        config.headers.Accept = 'application/json';
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        config.headers['Content-Type'] = 'application/json';

        const existing = findLinkedAccessoryLine(latestCart, variantId);
        let requestUrl;
        let useUpdate = false;

        if (existing?.key) {
          useUpdate = true;
          requestUrl = window.theme?.routes?.cart_update_url || `${window.Shopify?.routes?.root || '/'}cart/update.js`;
          config.body = JSON.stringify({
            updates: { [existing.key]: (Number(existing.quantity) || 0) + 1 },
            sections,
            sections_url: window.location.pathname
          });
        } else {
          requestUrl = window.theme?.routes?.cart_add_url || `${window.Shopify?.routes?.root || '/'}cart/add.js`;
          config.body = JSON.stringify({
            items: [{
              id: Number(variantId),
              quantity: 1,
              properties: {
                _hk_linked_accessory: 'true',
                _hk_linked_main_variant: String(activeMainVariantId || '')
              }
            }],
            sections,
            sections_url: window.location.pathname
          });
        }

        const response = await fetch(requestUrl, config);
        const parsed = await parseJsonResponse(response, useUpdate ? 'Update cart' : 'Add to cart');
        const cart = useUpdate ? parsed : cartStateFromSections(parsed);
        publishCartUpdate(cart, variantId);
        syncCards(cart);
      } catch (error) {
        if (status) {
          status.textContent = error?.message || 'Could not add this accessory. Please try again.';
          status.classList.add('is-error');
        }
      } finally {
        button?.removeAttribute('aria-busy');
        if (button) button.disabled = false;
        requestInFlight = false;
      }
    }

    form?.addEventListener('submit', (event) => {
      const variantId = String(new FormData(form).get('id') || '');
      if (!mainVariantIds.has(variantId)) return;
      lastSubmit = {
        variantId,
        quantity: Math.max(1, Number(new FormData(form).get('quantity')) || 1),
        submitter: event.submitter || document.activeElement,
        at: Date.now()
      };
    }, true);

    grid?.addEventListener('click', (event) => {
      const button = event.target.closest('[data-hk-upsell-add]');
      if (!button) return;
      event.preventDefault();
      const card = button.closest('[data-hk-upsell-card]');
      const variantId = button.dataset.variantId || cardVariantId(card);
      if (card && variantId) addAccessory(card, variantId);
    });

    grid?.addEventListener('change', (event) => {
      const select = event.target.closest('[data-hk-upsell-variant-select]');
      if (!select) return;
      const card = select.closest('[data-hk-upsell-card]');
      if (!card) return;
      const option = select.selectedOptions?.[0];
      const button = card.querySelector('[data-hk-upsell-add]');
      const image = card.querySelector('[data-hk-upsell-image]');
      const price = card.querySelector('[data-hk-upsell-price]');
      const compare = card.querySelector('[data-hk-upsell-compare-price]');

      if (button) button.dataset.variantId = select.value;
      if (price && option?.dataset.price) price.textContent = option.dataset.price;
      if (compare) {
        const value = String(option?.dataset.comparePrice || '').trim();
        compare.textContent = value;
        compare.hidden = !value;
      }
      if (image && option?.dataset.image) image.src = option.dataset.image;
      if (latestCart) setCardState(card, cartVariantQuantity(latestCart, select.value));
    });

    closeButtons.forEach((button) => button.addEventListener('click', (event) => {
      event.preventDefault();
      close();
    }));

    drawer?.addEventListener('drawer:afterHide', close);
    window.addEventListener('resize', updateDrawerOffset, { passive: true });

    if (drawer) {
      const observer = new MutationObserver(updateDrawerOffset);
      observer.observe(drawer, { attributes: true, attributeFilter: ['open', 'active', 'hidden'] });
      const inner = drawer.querySelector('.drawer__inner');
      if (inner && 'ResizeObserver' in window) {
        const resizeObserver = new ResizeObserver(updateDrawerOffset);
        resizeObserver.observe(inner);
      }
    }

    return {
      root,
      mainVariantIds,
      get lastSubmit() { return lastSubmit; },
      setCart(cart) { if (cart) { latestCart = cart; syncCards(cart); } },
      open,
      close,
      clearInlineSelections,
      destroy() {
        close();
      }
    };
  }

  function initRoot(root) {
    if (!root || root.dataset.hkUpsellV18Ready === 'true') return;
    root.dataset.hkUpsellV18Ready = 'true';

    if (root.parentElement !== document.body) document.body.appendChild(root);
    const controller = createController(root);
    if (controller) runtime.controllers.add(controller);
  }

  function connectPubSub() {
    if (runtime.pubsubUnsubscribe) return;
    const pubsub = window.theme?.pubsub;
    const eventName = pubsub?.PUB_SUB_EVENTS?.cartUpdate;
    if (!pubsub?.subscribe || !eventName) return;

    runtime.pubsubUnsubscribe = pubsub.subscribe(eventName, (event) => {
      if (event?.cart) {
        for (const controller of runtime.controllers) controller?.setCart(event.cart);
      }

      if (event?.source !== 'product-form') return;
      const variantId = String(event.productVariantId || '');
      if (!variantId) return;
      runtime.lastProductCartByVariant.set(variantId, event.cart || null);
    });
  }

  function handleMainAddSuccess(event) {
    const ids = extractAddedVariantIds(event?.detail?.product);
    let controller = findController(ids);

    if (!controller) {
      for (const candidate of runtime.controllers) {
        const submit = candidate?.lastSubmit;
        if (submit && Date.now() - submit.at < 2500 && candidate.mainVariantIds.has(submit.variantId)) {
          controller = candidate;
          break;
        }
      }
    }
    if (!controller) return;

    const submit = controller.lastSubmit;
    const variantId = submit?.variantId || [...ids].find((id) => controller.mainVariantIds.has(id)) || '';
    const quantity = submit?.quantity || 1;
    const cart = runtime.lastProductCartByVariant.get(String(variantId)) || null;

    controller.clearInlineSelections();

    // Concept calls CartDrawer.show() synchronously immediately after this event.
    // Open the companion panel on the next animation frame: by then the drawer
    // owns its native modal lifecycle, while this panel remains independent.
    requestAnimationFrame(() => {
      if (!controller.root?.isConnected) return;
      controller.open({ variantId, quantity, cart });
    });
  }

  function initAll() {
    document.querySelectorAll(ROOT_SELECTOR).forEach(initRoot);
    connectPubSub();

    for (const controller of [...runtime.controllers]) {
      if (!controller?.root?.isConnected) runtime.controllers.delete(controller);
    }
  }

  if (!runtime.initialized) {
    runtime.initialized = true;
    document.addEventListener('ajaxProduct:added', handleMainAddSuccess);
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      for (const controller of runtime.controllers) controller.close();
    });
    document.addEventListener('DOMContentLoaded', initAll);
    document.addEventListener('shopify:section:load', initAll);
    window.setTimeout(connectPubSub, 0);
  }

  initAll();
})();
